// Start again (proposed step 0.44, docs/specs/START_AGAIN.md): the preview the owner reads before wiping their business
// (crm_reset_preview), read tolerantly, and the plain-English lists of what goes and what stays.

export interface ResetTableCount {
  table: string;
  rows: number;
  why: string;
}

export interface ResetPreview {
  name: string;
  clear: ResetTableCount[];
  clear_rows: number;
  keep: ResetTableCount[];
  suppressions: number;
  contacted_kept: number;
  people_contacted: number;
  members: number;
  secrets: number;
  mailboxes: number;
  call_recordings: number;
  busy: string | null;
  next_allowed_at: string | null;
  unknown_tables: string[];
}

export interface ResetResult {
  /** "reset" when the business was wiped; "demo" when the demo only showed what would happen. */
  status: "reset" | "demo";
  cleared_rows: number;
  suppressions_kept: number;
  contacted_kept: number;
  call_recordings_left: number;
  next: string;
}

const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : typeof v === "string" && /^\d+$/.test(v) ? Number(v) : 0);
const str = (v: unknown) => (typeof v === "string" && v ? v : null);
const counts = (v: unknown): ResetTableCount[] =>
  Array.isArray(v)
    ? v.flatMap((x) => (x && typeof x === "object" && typeof (x as Record<string, unknown>).table === "string"
        ? [{ table: String((x as Record<string, unknown>).table), rows: num((x as Record<string, unknown>).rows), why: String((x as Record<string, unknown>).why ?? "") }]
        : []))
    : [];

export function readResetPreview(v: unknown): ResetPreview {
  const o = v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
  return {
    name: typeof o.name === "string" ? o.name : "",
    clear: counts(o.clear),
    clear_rows: num(o.clear_rows),
    keep: counts(o.keep),
    suppressions: num(o.suppressions),
    contacted_kept: num(o.contacted_kept),
    people_contacted: num(o.people_contacted),
    members: num(o.members),
    secrets: num(o.secrets),
    mailboxes: num(o.mailboxes),
    call_recordings: num(o.call_recordings),
    busy: str(o.busy),
    next_allowed_at: str(o.next_allowed_at),
    unknown_tables: Array.isArray(o.unknown_tables) ? o.unknown_tables.filter((x): x is string => typeof x === "string") : [],
  };
}

export function readResetResult(v: unknown): ResetResult {
  const o = v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
  return {
    status: o.status === "demo" ? "demo" : "reset",
    cleared_rows: num(o.cleared_rows),
    suppressions_kept: num(o.suppressions_kept),
    contacted_kept: num(o.contacted_kept),
    call_recordings_left: num(o.call_recordings_left),
    next: typeof o.next === "string" && o.next.startsWith("/") ? o.next : "/setup/core",
  };
}

/** The name as the database compares it: no case, no extra spaces (crm__reset_name). */
export function sameName(typed: string, name: string): boolean {
  const n = (s: string) => s.trim().replace(/\s+/g, " ").toLowerCase();
  return n(typed) !== "" && n(typed) === n(name);
}

/** How many rows a group of tables holds in the preview. */
export function rowsIn(p: ResetPreview, tables: string[]): number {
  return p.clear.filter((c) => tables.includes(c.table.replace(/^public\./, ""))).reduce((s, c) => s + c.rows, 0);
}

/** What starting again deletes, in plain words, with the tables each line counts. */
export const DELETED: { label: string; tables: string[] }[] = [
  { label: "Firms and people in your CRM, with their notes, tags, links and history", tables: ["organisations", "people", "notes", "identities", "person_links", "relationship_summaries"] },
  { label: "Emails, letters, calls and messages: drafts, sent and received, and every sequence", tables: ["outbox", "touches", "messages", "enrolments", "calls"] },
  { label: "Deals, meetings, offers and documents", tables: ["deals", "meetings", "deal_documents", "connect_offers"] },
  { label: "Your business core, settings, playbook and plays", tables: ["tenant_config_versions", "plays", "offer_workshops"] },
  { label: "Prospect searches, signals and sources", tables: ["prospect_searches", "prospect_results", "signals", "source_records"] },
  { label: "Website, content, social posts, newsletter, ads, listings and reviews", tables: ["sites", "site_pages", "content_items", "subscribers", "ad_campaigns", "listings", "reviews"] },
  { label: "Connected mailboxes, WhatsApp and accounts, and the keys Ogma kept for them", tables: ["mailboxes", "tenant_vendor_accounts", "whatsapp_accounts", "sending_domains"] },
  { label: "Tasks, engine runs, schedules and what runs by itself", tables: ["tasks", "agent_runs", "engine_schedules", "autonomy_levels", "autonomy_scopes"] },
  { label: "Documents and uploaded files (removed from storage within a day)", tables: ["tenant_documents", "history_uploads", "brand_assets"] },
];
